import { t } from "@/i18n-js/instance";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import {
  isTrialThenUpfrontThenValue,
  isTrialThenValue,
} from "@/react/helpers/paywallPriceHelpers";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { SkeletonLoader } from "@circle-react-shared/uikit/SkeletonLoader";
import { Typography } from "@circle-react-shared/uikit/Typography";

interface NextPaymentLineProps {
  className?: string;
}

const getPaymentDate = (date: string | undefined): string | undefined => {
  if (!date) return undefined;

  // eslint-disable-next-line sonarjs/prefer-regexp-exec -- Disabled for plugin version update. Fix when possible
  const regex = date.match(/(\d\d\d\d)-(\d\d)-(\d\d)/);

  if (regex) {
    const [, year, month, day] = regex;
    const dateGenerated = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    );

    return formattedDateTime({
      dateTime: dateGenerated,
      format: dateFormats.short_month_and_date_with_year,
    });
  }

  return undefined;
};

export const NextPaymentLine = ({ className }: NextPaymentLineProps) => {
  const { previewMutation } = usePaywallCheckoutContext();
  const preview = previewMutation.data;

  if (!preview || previewMutation.isLoading) {
    return <SkeletonLoader variant="row" lg className={className} />;
  }

  const nextPaymentLineString = [];

  const hasTax =
    (preview?.first_payment?.tax || 0) > 0 ||
    (preview?.future_payments?.tax || 0) > 0;

  if (hasTax) {
    nextPaymentLineString.push(t("tax_included"));
  }

  if (
    isTrialThenValue(preview.preview_scenario) ||
    isTrialThenUpfrontThenValue(preview.preview_scenario)
  ) {
    const dateString = getPaymentDate(preview.first_payment.due_on);
    if (dateString) {
      nextPaymentLineString.push(
        t("next_payment", {
          next_payment_date: dateString,
        }),
      );
    }
  } else {
    const dateString = getPaymentDate(preview.future_payments?.start_on);
    if (preview.future_payments && dateString) {
      nextPaymentLineString.push(
        t("next_payment", { next_payment_date: dateString }),
      );
    }
  }

  return (
    <div className={classNames("size-full", className)}>
      <Typography.BodyXs color="text-default">
        {nextPaymentLineString.join(" ")}
      </Typography.BodyXs>
    </div>
  );
};
